import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import ChatBox from '../../components/ChatBox';
import ReviewModal from '../../components/ReviewModal';
import { MapPin, Phone, AlertCircle, Maximize2, Navigation2 } from 'lucide-react';
import NavigationMap from '../../components/NavigationMap';
import DriverNavigationView from '../../components/driver/DriverNavigationView';
import { toast } from '../../hooks/use-toast';

// Custom hooks
import { useActiveRide } from '../../hooks/driver/useActiveRide';
import { useOTPVerification } from '../../hooks/driver/useOTPVerification';
import { useRideCancellation } from '../../hooks/driver/useRideCancellation';
import { useRideActions } from '../../hooks/driver/useRideActions';

// UI Components
import OTPDialog from '../../components/driver/OTPDialog';
import CancellationDialog from '../../components/driver/CancellationDialog';
import RideInfoCard from '../../components/driver/RideInfoCard';
import NavigationInfoCard from '../../components/driver/NavigationInfoCard';

const ActiveRide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showFullNavigation, setShowFullNavigation] = useState(!!location.state?.ride);
  
  // Initialize custom hooks
  const activeRide = useActiveRide(location.state?.ride || null);
  
  // Create ref to hold rideActions.handleStatusUpdate to avoid circular dependency
  const handleStatusUpdateRef = React.useRef(null);
  
  const otpVerification = useOTPVerification(
    activeRide.ride,
    async (otpType) => {
      // Refresh ride data after OTP verification
      await activeRide.fetchActiveRide();
      
      // If delivery OTP was verified, complete the ride
      if (otpType === 'delivery' && handleStatusUpdateRef.current) {
        handleStatusUpdateRef.current('complete');
      }
    }
  );
  
  const rideCancellation = useRideCancellation(
    activeRide.ride,
    () => navigate('/driver/rides')
  );
  
  const rideActions = useRideActions(
    activeRide.ride,
    otpVerification,
    (action, destination) => {
      if (action === 'complete') {
        navigate('/driver/history');
      } else if (action === 'dispute') {
        navigate('/driver/dashboard');
      } else if (action === 'refresh') {
        activeRide.fetchActiveRide();
      }
    }
  );
  
  // Update ref after rideActions is initialized
  React.useEffect(() => {
    handleStatusUpdateRef.current = rideActions.handleStatusUpdate;
  }, [rideActions.handleStatusUpdate]);

  // Check if no active ride after fetching
  if (activeRide.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading active ride...</p>
        </div>
      </div>
    );
  }

  if (!activeRide.ride) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No Active Ride</h2>
          <p className="text-gray-600 mb-4">You do not have any active rides at the moment</p>
          <Button onClick={() => navigate('/driver/rides')} className="bg-pink-600 hover:bg-pink-700">
            View Available Rides
          </Button>
        </Card>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      accepted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-green-100 text-green-800',
      arrived: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const callCustomer = () => {
    toast({
      title: 'Contact Customer',
      description: 'Customer contact feature coming soon',
    });
  };

  const nextAction = rideActions.getNextAction();

  // Handle call customer from navigation view
  const handleCallFromNav = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast({
        title: 'Contact Customer',
        description: 'Customer contact feature coming soon',
      });
    }
  };

  // Full-screen Navigation View
  if (showFullNavigation && activeRide.ride) {
    return (
      <>
        <DriverNavigationView
          ride={activeRide.ride}
          driverLocation={activeRide.driverLocation}
          onStatusUpdate={async (action) => {
            await rideActions.handleStatusUpdate(action);
            // Refresh ride data to update UI after status change
            if (action !== 'complete') {
              setTimeout(() => activeRide.fetchActiveRide(), 500);
            }
            // Close navigation on complete
            if (action === 'complete') {
              setShowFullNavigation(false);
            }
          }}
          onCall={handleCallFromNav}
          onClose={() => setShowFullNavigation(false)}
          isUpdating={rideActions.updating}
        />
        
        {/* OTP Dialog - needs to be accessible in nav view too */}
        {otpVerification.showOTPDialog && (
          <OTPDialog
            ride={activeRide.ride}
            otpType={otpVerification.otpType}
            otpCode={otpVerification.otpCode}
            setOtpCode={otpVerification.setOtpCode}
            verifyingOTP={otpVerification.verifyingOTP}
            onVerify={otpVerification.verifyOTP}
            onClose={otpVerification.closeOTPDialog}
          />
        )}

        {/* Review Modal */}
        <ReviewModal
          isOpen={rideActions.showReviewModal}
          onClose={() => {
            rideActions.setShowReviewModal(false);
            setShowFullNavigation(false);
            navigate('/driver/history');
          }}
          ride={activeRide.ride}
          userRole="driver"
          onSubmitSuccess={() => {
            rideActions.setShowReviewModal(false);
            setShowFullNavigation(false);
            navigate('/driver/history');
          }}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Active Ride</h1>
              <Badge className={`mt-1 ${getStatusBadgeColor(activeRide.ride.status)}`}>
                {activeRide.ride.status?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/driver/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Enhanced Navigation Map */}
          <div>
            {/* Navigation Phase Indicator */}
            {activeRide.ride && (
              <Card className={`p-3 mb-4 ${
                activeRide.ride.status === 'in_progress' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center gap-2">
                  <Navigation2 className={`h-5 w-5 ${
                    activeRide.ride.status === 'in_progress' 
                      ? 'text-green-600' 
                      : 'text-blue-600'
                  }`} />
                  <span className={`font-medium ${
                    activeRide.ride.status === 'in_progress' 
                      ? 'text-green-800' 
                      : 'text-blue-800'
                  }`}>
                    {activeRide.ride.status === 'in_progress' 
                      ? '📍 Navigating to Drop-off' 
                      : '🚗 Navigating to Pickup Point'}
                  </span>
                </div>
              </Card>
            )}

            <Card className="h-[500px] overflow-hidden">
              {(() => {
                const navPoints = activeRide.getNavigationPoints();
                return navPoints.origin && navPoints.destination ? (
                  <NavigationMap
                    pickup={navPoints.origin}
                    dropoff={navPoints.destination}
                    onRouteUpdate={activeRide.handleRouteUpdate}
                  />
                ) : (
                  <div className="relative w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading navigation...</p>
                    </div>
                  </div>
                );
              })()}
            </Card>

            {/* Navigation Button */}
            {(() => {
              const navPoints = activeRide.getNavigationPoints();
              const isPickupPhase = activeRide.ride?.status !== 'in_progress';
              
              return (
                <div className="mt-4">
                  {/* In-App Navigation Button - Primary & Only Option */}
                  <Button
                    onClick={() => setShowFullNavigation(true)}
                    className={`w-full text-white py-6 ${
                      isPickupPhase 
                        ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700' 
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    }`}
                    data-testid="open-in-app-navigation-btn"
                  >
                    <Maximize2 className="mr-2 h-5 w-5" />
                    {isPickupPhase ? 'Navigate to Pickup' : 'Navigate to Drop-off'}
                  </Button>
                </div>
              );
            })()}
          </div>

          {/* Right: Ride Details & Actions */}
          <div className="space-y-6">
            {/* Payment Method Indicator */}
            {activeRide.ride.paymentMethod === 'cash' && (
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">💵</div>
                  <div className="flex-1">
                    <p className="font-semibold text-green-900">Cash Payment</p>
                    <p className="text-sm text-green-700">
                      Collect K{activeRide.ride.price?.toFixed(2)} from rider at ride completion
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Ride Details */}
            <RideInfoCard ride={activeRide.ride} />

            {/* Navigation Info */}
            <NavigationInfoCard navigationInfo={activeRide.navigationInfo} />

            {/* Customer Contact */}
            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4">Customer</h2>
              <div className="space-y-3">
                {activeRide.ride.userName && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                      {activeRide.ride.userName?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{activeRide.ride.userName}</p>
                      {activeRide.ride.userPhone && (
                        <p className="text-sm text-gray-600">{activeRide.ride.userPhone}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {activeRide.ride.userPhone && (
                  <a 
                    href={`tel:${activeRide.ride.userPhone}`}
                    className="block"
                  >
                    <Button
                      variant="outline"
                      className="w-full"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Call {activeRide.ride.userPhone}
                    </Button>
                  </a>
                )}
                
                {!activeRide.ride.userPhone && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={callCustomer}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Contact Customer
                  </Button>
                )}
              </div>
            </Card>

            {/* Action Buttons */}
            {nextAction && (
              <Card className="p-6">
                <h2 className="text-lg font-bold mb-4">Next Action</h2>
                <Button
                  onClick={() => rideActions.handleStatusUpdate(nextAction.action)}
                  disabled={rideActions.updating}
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg"
                >
                  {rideActions.updating ? 'Updating...' : nextAction.label}
                </Button>

                {activeRide.ride.status === 'accepted' && (
                  <>
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      Click when the customer is in your vehicle and ready to go
                    </p>
                    <Button
                      onClick={rideCancellation.openCancelDialog}
                      variant="outline"
                      className="w-full mt-3 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="driver-cancel-accepted-btn"
                    >
                      Cancel Ride
                    </Button>
                  </>
                )}
                {activeRide.ride.status === 'in_progress' && (
                  <>
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      Click when you have reached the destination
                    </p>
                    <Button
                      onClick={rideCancellation.openCancelDialog}
                      variant="outline"
                      className="w-full mt-3 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="driver-cancel-inprogress-btn"
                    >
                      Cancel Ride
                    </Button>
                  </>
                )}
              </Card>
            )}

            {/* Emergency */}
            <Card className="p-6 bg-red-50 border-red-200">
              <div className="flex items-center gap-3 text-red-800">
                <AlertCircle className="h-5 w-5" />
                <div className="flex-1">
                  <p className="font-medium">Need Help?</p>
                  <p className="text-sm">Contact support for emergency assistance</p>
                </div>
                <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100">
                  Call Support
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Chat Box */}
      {activeRide.ride && activeRide.ride.userId && (
        <ChatBox
          rideId={activeRide.ride.id}
          currentUserId={JSON.parse(localStorage.getItem('tekstar_user') || '{}').id}
          otherUserName="Rider"
          otherUserRole="Your Customer"
        />
      )}

      {/* OTP Verification Dialog */}
      {otpVerification.showOTPDialog && (
        <OTPDialog
          ride={activeRide.ride}
          otpType={otpVerification.otpType}
          otpCode={otpVerification.otpCode}
          setOtpCode={otpVerification.setOtpCode}
          verifyingOTP={otpVerification.verifyingOTP}
          onVerify={otpVerification.verifyOTP}
          onClose={otpVerification.closeOTPDialog}
        />
      )}

      {/* Cancellation Dialog */}
      {rideCancellation.showCancelDialog && (
        <CancellationDialog
          cancellationReason={rideCancellation.cancellationReason}
          setCancellationReason={rideCancellation.setCancellationReason}
          cancelling={rideCancellation.cancelling}
          onCancel={rideCancellation.handleCancelRide}
          onClose={rideCancellation.closeCancelDialog}
        />
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={rideActions.showReviewModal}
        onClose={() => {
          rideActions.setShowReviewModal(false);
          navigate('/driver/history');
        }}
        ride={activeRide.ride}
        userRole="driver"
        onSubmitSuccess={() => {
          rideActions.setShowReviewModal(false);
          navigate('/driver/history');
        }}
      />
    </div>
  );
};

export default ActiveRide;
